import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { cn } from '../tokens/cn';
import { Label } from '../primitives/label';
import { Tooltip } from '../primitives/tooltip';

type Status = 'error' | 'success' | 'info' | null;

const statusStyles: Record<
  NonNullable<Status>,
  { color: string; Icon: typeof IconAlertCircle; role: 'alert' | 'status' }
> = {
  error: {
    color: 'text-[var(--tk-danger-fg-on-subtle)]',
    Icon: IconAlertCircle,
    role: 'alert',
  },
  success: {
    color: 'text-[var(--tk-success-fg-on-subtle)]',
    Icon: IconCheck,
    role: 'status',
  },
  info: {
    color: 'text-[var(--tk-info-fg-on-subtle)]',
    Icon: IconInfoCircle,
    role: 'status',
  },
};

export interface FormFieldProps {
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  optionalText?: string;
  helperText?: ReactNode;
  errorMessage?: ReactNode;
  successMessage?: ReactNode;
  infoMessage?: ReactNode;
  className?: string;
  /**
   * When true, render `helperText` as an info-icon tooltip next to the label
   * instead of a paragraph below the input. Useful for long help strings that
   * crowd the form layout. Errors/success/info messages still render below.
   */
  helpAsTooltip?: boolean;
  /**
   * Single field control. Receives `id`, `aria-invalid`, `aria-describedby` automatically.
   */
  children: ReactElement<{
    id?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
  }>;
}

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      htmlFor,
      required,
      optionalText,
      helperText,
      errorMessage,
      successMessage,
      infoMessage,
      className,
      helpAsTooltip = false,
      children,
    },
    ref,
  ) => {
    const generatedId = useId();
    const fieldId = htmlFor ?? generatedId;
    const helperId = `${fieldId}-helper`;
    const messageId = `${fieldId}-message`;

    const status: Status = errorMessage
      ? 'error'
      : successMessage
        ? 'success'
        : infoMessage
          ? 'info'
          : null;
    const message = errorMessage ?? successMessage ?? infoMessage;
    const describedByParts: string[] = [];
    if (helperText) describedByParts.push(helperId);
    if (message) describedByParts.push(messageId);
    const describedBy =
      describedByParts.length > 0 ? describedByParts.join(' ') : undefined;

    const child = isValidElement(children)
      ? cloneElement(children, {
          id: children.props.id ?? fieldId,
          'aria-invalid': status === 'error' || children.props['aria-invalid'],
          'aria-describedby':
            [children.props['aria-describedby'], describedBy]
              .filter(Boolean)
              .join(' ') || undefined,
        })
      : children;

    const useTooltip = helpAsTooltip && Boolean(helperText);

    return (
      <div ref={ref} className={cn('flex flex-col gap-2', className)}>
        {label && (
          <div className="flex items-center gap-1.5">
            <Label
              htmlFor={fieldId}
              required={required}
              optionalText={optionalText}
            >
              {label}
            </Label>
            {useTooltip && (
              <Tooltip content={helperText} side="top" align="start">
                <button
                  type="button"
                  aria-label="More info"
                  aria-describedby={helperId}
                  className={cn(
                    'inline-flex items-center justify-center h-4 w-4 rounded-full',
                    'text-[var(--tk-surface-fg-muted)] hover:text-[var(--tk-surface-fg)]',
                    'transition-colors cursor-help',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tk-brand-ring)]',
                  )}
                >
                  <IconInfoCircle size={14} aria-hidden />
                  <span id={helperId} className="sr-only">
                    {helperText}
                  </span>
                </button>
              </Tooltip>
            )}
          </div>
        )}
        {child}
        {!useTooltip && helperText && !message && (
          <span
            id={helperId}
            className={cn(
              'text-sm text-[var(--tk-surface-fg-muted)]',
              'animate-fade-up animate-duration-200 animate-ease-out',
            )}
          >
            {helperText}
          </span>
        )}
        {message && status && (
          <span
            id={messageId}
            role={statusStyles[status].role}
            className={cn(
              'text-sm font-medium inline-flex items-start gap-1.5',
              statusStyles[status].color,
              'animate-fade-up animate-duration-300 animate-ease-out',
            )}
          >
            {(() => {
              const Icon = statusStyles[status].Icon;
              return <Icon size={16} aria-hidden className="mt-0.5 shrink-0" />;
            })()}
            <span className="leading-snug">{message}</span>
          </span>
        )}
      </div>
    );
  },
);

FormField.displayName = 'FormField';
